/**
 * External Configuration for StudentDashboard
 * Centralized configuration for external links and contact information
 * Supports environment variable overrides for different environments
 */

// Default URLs - can be overridden by environment variables
const DEFAULT_URLS = {
  SCHOLARS_PROGRAM: 'https://honors-scholars.osu.edu/scholars',
  HONORS_PROGRAM: 'https://honors-scholars.osu.edu/honors',
  UNDERGRADUATE_ADMISSIONS: 'https://undergrad.osu.edu',
  APPLICANT_CENTER: 'https://apply.osu.edu',
  LEGACY_SCHOLARS: 'https://hsapp-dev.webtest.osu.edu/HonorsAndScholars/Secure/Confirmation.aspx'
};

// Default contact information - can be overridden by environment variables
const DEFAULT_CONTACT_INFO = {
  // Primary program contact
  honorsScholarsEmail: 'honors-scholars@osu.edu',
  honorsScholarsPhone: '(614) 292-3135',

  // Admissions contact
  admissionsEmail: 'askabuckeye@osu.edu',
  admissionsPhone: '(614) 292-3980'
};

/**
 * Get external URL with environment variable override support
 * @param {string} key - URL key from DEFAULT_URLS
 * @param {string} envVar - Environment variable name to check
 * @returns {string} URL from environment or default
 */
const getExternalUrl = (key, envVar) => {
  // Check environment variable first, then fall back to default
  return process.env[envVar] || DEFAULT_URLS[key];
};

/**
 * Get contact information with environment variable override support
 * @returns {Object} Contact information with environment overrides applied
 */
export const getContactInfo = () => {
  const contactInfo = {
    // Primary program contact - check environment variables first
    honorsScholarsEmail: process.env.REACT_APP_HONORS_SCHOLARS_EMAIL || DEFAULT_CONTACT_INFO.honorsScholarsEmail,
    honorsScholarsPhone: process.env.REACT_APP_HONORS_SCHOLARS_PHONE || DEFAULT_CONTACT_INFO.honorsScholarsPhone,

    // Admissions contact - check environment variables first
    admissionsEmail: process.env.REACT_APP_ADMISSIONS_EMAIL || DEFAULT_CONTACT_INFO.admissionsEmail,
    admissionsPhone: process.env.REACT_APP_ADMISSIONS_PHONE || DEFAULT_CONTACT_INFO.admissionsPhone
  };

  // Derived contact patterns
  contactInfo.honorsScholarsContact = `${contactInfo.honorsScholarsEmail} or ${contactInfo.honorsScholarsPhone}`;
  contactInfo.admissionsContact = `${contactInfo.admissionsEmail} or ${contactInfo.admissionsPhone}`;

  // Contact patterns for different contexts
  contactInfo.patterns = {
    questionsContact: `Questions? Contact us at ${contactInfo.honorsScholarsContact}`,
    questionsContactAdmissions: `Questions about enrollment? Contact Undergraduate Admissions at ${contactInfo.admissionsContact}`,
    officeContact: `University Honors & Scholars Center: ${contactInfo.honorsScholarsContact}`,
    generalQuestions: `Please contact the University Honors & Scholars Center at ${contactInfo.honorsScholarsContact}`,
    statusQuestions: `If you have questions about this status, please contact the University Honors & Scholars Center at ${contactInfo.honorsScholarsContact}`
  };

  return contactInfo;
};

/**
 * External URLs configuration
 * Supports environment variable overrides for different environments
 */
export const EXTERNAL_URLS = {
  SCHOLARS_PROGRAM: getExternalUrl('SCHOLARS_PROGRAM', 'REACT_APP_SCHOLARS_URL'),
  HONORS_PROGRAM: getExternalUrl('HONORS_PROGRAM', 'REACT_APP_HONORS_URL'),
  UNDERGRADUATE_ADMISSIONS: getExternalUrl('UNDERGRADUATE_ADMISSIONS', 'REACT_APP_UNDERGRAD_ADMISSIONS_URL'),
  APPLICANT_CENTER: getExternalUrl('APPLICANT_CENTER', 'REACT_APP_APPLICANT_CENTER_URL'),
  LEGACY_SCHOLARS: getExternalUrl('LEGACY_SCHOLARS', 'REACT_APP_LEGACY_SCHOLARS_URL')
};

/**
 * Action types for external navigation
 */
export const EXTERNAL_ACTION_TYPES = {
  EXPLORE_SCHOLARS: 'explore-scholars',
  EXPLORE_HONORS: 'explore-honors',
  CONTACT_ADMISSIONS: 'contact-admissions',
  VISIT_APPLICANT_CENTER: 'visit-applicant-center',
  ACCEPT_INVITATION: 'accept-invitation'
};

/**
 * Check if legacy scholars is enabled
 * @returns {boolean} True if legacy scholars is enabled
 */
export const isLegacyScholarsEnabled = () => {
  return process.env.REACT_APP_ENABLE_LEGACY_SCHOLARS === 'true';
};

/**
 * Get URL for a specific action type
 * @param {string} actionType - Action type from EXTERNAL_ACTION_TYPES
 * @returns {string} External URL or special indicator for inline actions
 */
export const getActionUrl = (actionType) => {
  const urlMap = {
    [EXTERNAL_ACTION_TYPES.EXPLORE_SCHOLARS]: EXTERNAL_URLS.SCHOLARS_PROGRAM,
    [EXTERNAL_ACTION_TYPES.EXPLORE_HONORS]: EXTERNAL_URLS.HONORS_PROGRAM,
    [EXTERNAL_ACTION_TYPES.CONTACT_ADMISSIONS]: EXTERNAL_URLS.UNDERGRADUATE_ADMISSIONS,
    [EXTERNAL_ACTION_TYPES.VISIT_APPLICANT_CENTER]: EXTERNAL_URLS.APPLICANT_CENTER,
    [EXTERNAL_ACTION_TYPES.ACCEPT_INVITATION]: isLegacyScholarsEnabled()
      ? EXTERNAL_URLS.LEGACY_SCHOLARS
      : 'INLINE_FORM' // Special indicator for inline form
  };

  return urlMap[actionType] || '#';
};

export default EXTERNAL_URLS;